import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { z } from 'zod';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Node,
  Edge,
  NodeChange,
  EdgeChange,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  MarkerType,
  ReactFlowInstance,
  OnSelectionChangeParams,
} from 'reactflow';
import { toast } from 'sonner';
import { nodeTypeConfig } from '@/components/workflow/WorkflowNodeTypes';
import { getCurrentWorkflowUserId, buildWorkflowApiRoute } from '@/utils/workflow-utils';
import { workflowApi } from '@/services/api/workflowApi';
import { useOrganizationStore } from '@/stores/organization/organization-store';
import { useNodeRegistryStore } from '@/stores/workflow/node-registry-store';
import { useDeviceStore, Device, ExecutionStep, ExecutionLog } from '@/stores/devices/device-store';
import { Screen, useScreenStore } from '@/stores/screens/screen-store';
import { useSchemaStore, SchemaDefinition } from '@/stores/schemas/schema-store';
import { useEditorStore } from '@/stores/editor/editor-store';
import { engineApi } from '@/services/api/engineApi';
import { WorkflowEnvironmentsDialog } from '@/components/workflow/WorkflowEnvironmentsDialog';
import { ExecutionTarget, DeviceVariantId, NodeDefinition, NodesByCategory } from '@/types/workflow-nodes';
import {
  JsonValue,
  WorkflowAvailableVariable,
  WorkflowDefinition,
  WorkflowDefinitionVariable,
  WorkflowNodeConfig,
  WorkflowNodeData,
  WorkflowValidationResult,
  WorkflowVariableMapping,
} from '@/types/workflow-editor';
import { WorkflowEditorContext } from './WorkflowEditorContext.shared';

// ─── Context Value ──────────────────────────────────────────────────────────

export interface WorkflowEditorContextValue {
  // Core workflow state
  workflowId: string | undefined;
  workflow: WorkflowDefinition | null;
  setWorkflow: React.Dispatch<React.SetStateAction<WorkflowDefinition | null>>;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  embedded: boolean;
  projectId: string | undefined;

  // ReactFlow state
  nodes: Node<WorkflowNodeData>[];
  setNodes: React.Dispatch<React.SetStateAction<Node<WorkflowNodeData>[]>>;
  onNodesChange: (changes: NodeChange[]) => void;
  edges: Edge[];
  setEdges: React.Dispatch<React.SetStateAction<Edge[]>>;
  onEdgesChange: (changes: EdgeChange[]) => void;

  // Selection
  selectedNodes: Node<WorkflowNodeData>[];
  setSelectedNodes: React.Dispatch<React.SetStateAction<Node<WorkflowNodeData>[]>>;
  selectedNode: Node<WorkflowNodeData> | null;

  // Variables
  workflowVariables: WorkflowDefinitionVariable[];
  setWorkflowVariables: React.Dispatch<React.SetStateAction<WorkflowDefinitionVariable[]>>;
  allAvailableVariables: WorkflowAvailableVariable[];

  // Tab state
  rightSidebarTab: 'properties' | 'variables' | 'execution' | 'ai-chat' | 'modification-log';
  setRightSidebarTab: React.Dispatch<React.SetStateAction<'properties' | 'variables' | 'execution' | 'ai-chat' | 'modification-log'>>;
  leftSidebarTab: 'nodes' | 'screens' | 'schemas' | 'secrets';
  setLeftSidebarTab: React.Dispatch<React.SetStateAction<'nodes' | 'screens' | 'schemas' | 'secrets'>>;

  // Validation
  validationResult: WorkflowValidationResult | null;
  setValidationResult: React.Dispatch<React.SetStateAction<WorkflowValidationResult | null>>;
  isValidating: boolean;
  showVariablePicker: boolean;
  setShowVariablePicker: React.Dispatch<React.SetStateAction<boolean>>;
  expressionInputRef: React.RefObject<HTMLInputElement>;

  // Debug
  debugModeEnabled: boolean;
  setDebugModeEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  serverDebugMode: boolean;
  lastExecutionId: string | null;
  nodeDebugState: Map<string, { lastStatus: 'completed' | 'error' | 'started' | 'skipped' | null; executionCount: number }>;
  sampleParseError: string | null;
  setSampleParseError: React.Dispatch<React.SetStateAction<string | null>>;
  effectiveExecutionKey: string | undefined;
  onDemandSchema: SchemaDefinition | null;

  // Device
  selectedDeviceId: string | null;
  setSelectedDeviceId: React.Dispatch<React.SetStateAction<string | null>>;
  selectedVariantId: DeviceVariantId | null;
  setSelectedVariantId: React.Dispatch<React.SetStateAction<DeviceVariantId | null>>;

  // Store data
  nodeRegistryLoading: boolean;
  selectedTarget: ExecutionTarget;
  setSelectedTarget: (target: ExecutionTarget) => void;
  isDataSource: boolean;
  setIsDataSource: (value: boolean) => void;
  getFilteredNodesByCategory: () => NodesByCategory[];
  getNodeDefinition: (type: string) => NodeDefinition | undefined;
  devices: Device[];
  deployWorkflow: (deviceId: string, workflowId: string) => Promise<{ success: boolean; queued?: boolean }>;
  getExecutionSteps: (deviceId: string) => ExecutionStep[];
  clearExecutionLogs: (key: string) => void;
  setDeviceLogging: (deviceId: string, enabled: boolean) => void;
  executionSteps: Map<string, ExecutionStep[]>;
  executionLogs: Map<string, ExecutionLog[]>;
  dataFilterOptions: {
    dataTypes: string[];
    esp32WorkflowIds: string[];
    customIds: string[];
    esp32Workflows: Array<{ id: string; name: string; status: string }>;
  };
  fetchDataFilterOptions: () => void;
  screens: Screen[];
  fetchScreens: () => void;
  openSchemaBuilder: () => void;
  schemas: SchemaDefinition[];
  fetchSchemas: () => void;
  getSchema: (id: string) => Promise<SchemaDefinition | null>;

  // Refs
  reactFlowWrapper: React.RefObject<HTMLDivElement>;

  // Handlers
  onConnect: (params: Connection) => void;
  onNodeClick: (event: React.MouseEvent, node: Node) => void;
  onPaneClick: () => void;
  onSelectionChange: (params: OnSelectionChangeParams) => void;
  onEdgeClick: (event: React.MouseEvent, edge: Edge) => void;
  onEdgesDelete: (edges: Edge[]) => void;
  onReconnect: (oldEdge: Edge, newConnection: Connection) => void;
  onInit: (instance: ReactFlowInstance) => void;
  onDragOver: (event: React.DragEvent) => void;
  onDrop: (event: React.DragEvent) => void;
  handleSave: () => Promise<boolean>;
  handleExecute: () => Promise<void>;
  handleCancelExecution: () => Promise<void>;
  isExecuting: boolean;
  handleBack: () => void;
  handleDeployToDevice: () => Promise<void>;
  handlePublishToggle: (shouldPublish: boolean) => Promise<void>;
  handleParseSampleMessage: () => void;
  handleValidateExpression: () => Promise<void>;
  handleInsertVariable: (variableName: string) => void;
  toggleServerDebugMode: (enabled: boolean) => Promise<void>;
  deleteSelectedNodes: () => void;
  updateNodeData: (field: string, value: JsonValue | undefined) => void;
  updateNodeParameter: (paramName: string, value: JsonValue | undefined) => void;
  updateNodeConfig: <K extends keyof WorkflowNodeConfig>(configName: K, value: WorkflowNodeConfig[K]) => void;
  getAvailableVariables: () => Array<{ name: string; type: string; source: string }>;
  extractJsonPaths: (obj: unknown, prefix?: string) => string[];
  pathToVariableName: (path: string) => string;

  // Environments dialog
  environmentsDialogOpen: boolean;
  openEnvironmentsDialog: () => void;
  closeEnvironmentsDialog: () => void;
  availableEnvironments: string[];
  handleSaveEnvironments: (environments: string[]) => Promise<void>;

  // Agent workflow
  convertToAgent: () => Promise<void>;

  // Close handler (for embedded mode)
  onClose?: () => void;
}



// ─── Provider Props ─────────────────────────────────────────────────────────

interface WorkflowEditorProviderProps {
  children: React.ReactNode;
  workflowId?: string;
  embedded?: boolean;
  onClose?: () => void;
  projectId?: string;
}

// ─── Provider Component ─────────────────────────────────────────────────────

export const WorkflowEditorProvider: React.FC<WorkflowEditorProviderProps> = ({
  children,
  workflowId: externalWorkflowId,
  embedded = false,
  onClose,
  projectId: externalProjectId,
}) => {
  const params = useParams<{ workflowId: string }>();
  const workflowId = externalWorkflowId || params.workflowId;
  const projectId = externalProjectId;
  const navigate = useNavigate();
  const { currentContext } = useOrganizationStore();
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const reactFlowInstanceRef = useRef<ReactFlowInstance | null>(null);

  // Node registry store
  const {
    initialize: initializeNodeRegistry,
    isInitialized: nodeRegistryInitialized,
    isLoading: nodeRegistryLoading,
    selectedTarget,
    setSelectedTarget,
    getFilteredNodesByCategory,
    getNode: getNodeDefinition,
  } = useNodeRegistryStore();

  // Device store
  const {
    devices,
    initialize: initializeDevices,
    isInitialized: devicesInitialized,
    deployWorkflow,
    getExecutionSteps,
    clearExecutionLogs,
    setDeviceLogging,
    executionSteps,
    executionLogs,
    dataFilterOptions,
    fetchDataFilterOptions,
    subscribeToWorkflow,
    unsubscribeFromWorkflow,
    isSocketConnected,
  } = useDeviceStore();

  // Screen store
  const { screens, fetchScreens, setWorkflowContext } = useScreenStore();

  // Schema store
  const { openBuilder: openSchemaBuilder, schemas, fetchSchemas, getSchema } = useSchemaStore();

  // On-demand schema fetch for compose_schema nodes
  const [onDemandSchema, setOnDemandSchema] = useState<SchemaDefinition | null>(null);

  // Device state
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
  const [selectedVariantId, setSelectedVariantId] = useState<DeviceVariantId | null>(null);

  // ReactFlow state
  const [nodes, setNodes, onNodesChange] = useNodesState<WorkflowNodeData>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const nodesRef = useRef<Node<WorkflowNodeData>[]>([]);
  const loadWorkflowRef = useRef<(id: string) => Promise<void>>(async () => {});
  const [selectedNodes, setSelectedNodes] = useState<Node<WorkflowNodeData>[]>([]);
  const selectedNode = useMemo(() => {
    if (selectedNodes.length !== 1) return null;
    return nodes.find(n => n.id === selectedNodes[0].id) || null;
  }, [selectedNodes, nodes]);

  // Core state
  const [workflow, setWorkflow] = useState<WorkflowDefinition | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [workflowVariables, setWorkflowVariables] = useState<WorkflowDefinitionVariable[]>([]);

  // Tab state
  const [rightSidebarTab, setRightSidebarTab] = useState<'properties' | 'variables' | 'execution' | 'ai-chat' | 'modification-log'>('properties');
  const [leftSidebarTab, setLeftSidebarTab] = useState<'nodes' | 'screens' | 'schemas' | 'secrets'>('nodes');

  // Validation
  const [validationResult, setValidationResult] = useState<WorkflowValidationResult | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [showVariablePicker, setShowVariablePicker] = useState(false);
  const expressionInputRef = useRef<HTMLInputElement>(null);

  // Debug state
  const [debugModeEnabled, setDebugModeEnabled] = useState(false);
  const [serverDebugMode, setServerDebugMode] = useState(false);
  const [isDataSource, setIsDataSource] = useState(false);
  const [lastExecutionId, setLastExecutionId] = useState<string | null>(null);
  const [isExecuting, setIsExecuting] = useState(false);
  const executionStartedAtRef = useRef<number>(0);
  const [sampleParseError, setSampleParseError] = useState<string | null>(null);
  const [nodeDebugState, setNodeDebugState] = useState<Map<string, {
    lastStatus: 'completed' | 'error' | 'started' | 'skipped' | null;
    executionCount: number;
  }>>(new Map());

  useEffect(() => {
    nodesRef.current = nodes;
  }, [nodes]);

  // ─── Derived State ──────────────────────────────────────────────────────

  const allAvailableVariables = useMemo(() => {
    let startNode = nodes.find((n) => n.data?.type === 'start');
    if (selectedNode?.data?.type === 'start') {
      startNode = selectedNode;
    }

    const mqttMappings = Array.isArray(startNode?.data?.config?.variableMappings)
      ? startNode.data.config.variableMappings
      : [];
    const mqttVariables = mqttMappings
      .filter((mapping: WorkflowVariableMapping) => mapping.targetVariable.trim() !== '')
      .map((mapping: WorkflowVariableMapping) => ({
        name: mapping.targetVariable.trim(),
        type: 'string' as const,
        defaultValue: undefined,
        source: 'mqtt' as const,
        sourcePath: mapping.sourcePath
      }));

    const manualVariables = workflowVariables.map(v => ({
      ...v,
      source: 'manual' as const
    }));

    const composeSchemaVariables = nodes
      .filter((n) => n.data?.type === 'compose_schema' && typeof n.data?.config?.outputVariable === 'string')
      .map((n) => ({
        name: n.data?.config?.outputVariable ?? '',
        type: 'object' as const,
        defaultValue: undefined,
        source: 'compose_schema' as const,
        sourceNode: n.id
      }));

    const mathOpVariables = nodes
      .filter((n) => n.data?.type === 'variable_math' && typeof n.data?.config?.resultVariable === 'string')
      .map((n) => ({
        name: n.data?.config?.resultVariable ?? '',
        type: 'number' as const,
        defaultValue: undefined,
        source: 'math_operation' as const,
        sourceNode: n.id
      }));

    return [...mqttVariables, ...manualVariables, ...composeSchemaVariables, ...mathOpVariables];
  }, [nodes, workflowVariables, selectedNode]);

  const effectiveExecutionKey = useMemo(() => {
    if (!serverDebugMode) {
      return debugModeEnabled ? selectedDeviceId || undefined : undefined;
    }
    if (lastExecutionId) return lastExecutionId;
    if (!workflowId) return undefined;

    let latestKey: string | undefined;
    let latestTimestamp = 0;
    executionSteps.forEach((steps, key) => {
      const workflowSteps = steps.filter(s => s.workflowId === workflowId);
      if (workflowSteps.length > 0) {
        const lastStep = workflowSteps[workflowSteps.length - 1];
        if (lastStep.timestamp > latestTimestamp) {
          latestTimestamp = lastStep.timestamp;
          latestKey = key;
        }
      }
    });
    return latestKey;
  }, [serverDebugMode, debugModeEnabled, selectedDeviceId, lastExecutionId, workflowId, executionSteps]);

  // ─── Effects ────────────────────────────────────────────────────────────

  // ESC key handler for embedded mode
  useEffect(() => {
    if (!embedded || !onClose) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [embedded, onClose]);

  // Sync workflow name to editor-store
  useEffect(() => {
    if (workflow?.name) {
      useEditorStore.setState({ workflowName: workflow.name });
    }
  }, [workflow?.name]);

  // Initialize stores
  useEffect(() => {
    if (!nodeRegistryInitialized) initializeNodeRegistry();
    if (!devicesInitialized) initializeDevices();
  }, [nodeRegistryInitialized, devicesInitialized, initializeNodeRegistry, initializeDevices]);

  useEffect(() => { fetchScreens(); }, [fetchScreens]);
  useEffect(() => { fetchSchemas(); }, [fetchSchemas]);

  // On-demand schema fetch for compose_schema nodes
  useEffect(() => {
    if (
      selectedNode?.data?.type === 'compose_schema' &&
      selectedNode.data.config?.schemaId
    ) {
      const schemaId = selectedNode.data.config.schemaId;
      const inCache = schemas.some((s) => s.id === schemaId);
      if (!inCache) {
        getSchema(schemaId).then((result) => {
          if (result) setOnDemandSchema(result);
        });
      } else {
        setOnDemandSchema(null);
      }
    }
  }, [selectedNode?.id, selectedNode?.data?.type, selectedNode?.data?.config?.schemaId, schemas, getSchema]);

  useEffect(() => { fetchDataFilterOptions(); }, [fetchDataFilterOptions]);

  // Load workflow
  useEffect(() => {
    if (workflowId) {
      void loadWorkflowRef.current(workflowId);
    } else {
      setIsLoading(false);
    }
  }, [workflowId, currentContext?.type, currentContext?.orgSlug]);

  // Sync workflow context to screen store
  useEffect(() => {
    if (workflow) {
      setWorkflowContext(workflow.id || workflow._id, selectedVariantId);
    } else {
      setWorkflowContext(null, null);
    }
  }, [workflow, selectedVariantId, setWorkflowContext]);

  useEffect(() => {
    if (workflow) fetchScreens();
  }, [workflow, fetchScreens]);

  // Handle custom edge delete events
  useEffect(() => {
    const handleDeleteEdge = (event: Event) => {
      if (!(event instanceof CustomEvent)) {
        return;
      }
      const detail: unknown = event.detail;
      const edgeId =
        typeof detail === 'object' &&
        detail !== null &&
        'id' in detail &&
        typeof detail.id === 'string'
          ? detail.id
          : undefined;
      if (edgeId) {
        setEdges((eds) => eds.filter((e) => e.id !== edgeId));
      }
    };
    window.addEventListener('deleteEdge', handleDeleteEdge);
    return () => window.removeEventListener('deleteEdge', handleDeleteEdge);
  }, [setEdges]);

  // Subscribe to workflow execution events
  useEffect(() => {
    if (!workflowId) return;
    if (serverDebugMode && isSocketConnected) {
      subscribeToWorkflow(workflowId);
    } else if (!serverDebugMode) {
      unsubscribeFromWorkflow(workflowId);
    }
    return () => {
      if (workflowId) unsubscribeFromWorkflow(workflowId);
    };
  }, [workflowId, serverDebugMode, isSocketConnected, subscribeToWorkflow, unsubscribeFromWorkflow]);

  // Process server workflow execution steps into debug state
  useEffect(() => {
    if (!serverDebugMode || !workflowId) return;

    const allSteps: ExecutionStep[] = [];
    let latestExecutionId: string | null = null;
    let latestTimestamp = 0;

    executionSteps.forEach((steps, key) => {
      const workflowSteps = steps.filter(s => s.workflowId === workflowId);
      if (workflowSteps.length > 0) {
        allSteps.push(...workflowSteps);
        const lastStep = workflowSteps[workflowSteps.length - 1];
        if (lastStep.timestamp > latestTimestamp) {
          latestTimestamp = lastStep.timestamp;
          latestExecutionId = lastStep.executionId || key;
        }
      }
    });

    if (latestExecutionId && latestExecutionId !== lastExecutionId) {
      setLastExecutionId(latestExecutionId);
    }

    if (allSteps.length === 0) return;

    const debugState = new Map<string, { lastStatus: 'completed' | 'error' | 'started' | 'skipped' | null; executionCount: number }>();
    for (const step of allSteps) {
      const current = debugState.get(step.nodeId) || { lastStatus: null, executionCount: 0 };
      if (step.status === 'completed' || step.status === 'error') {
        current.executionCount++;
        current.lastStatus = step.status;
      } else if (step.status === 'started') {
        current.lastStatus = step.status;
      }
      debugState.set(step.nodeId, current);
    }

    setNodeDebugState(debugState);
    setNodes(prev => prev.map(node => {
      const debug = debugState.get(node.id);
      return {
        ...node,
        data: {
          ...node.data,
          debugStatus: debug?.lastStatus || null,
          executionCount: debug?.executionCount || 0,
        }
      };
    }));

    // Detect execution completion: non-looping end node completed, or any node errored
    // Only check steps that arrived AFTER the current execution was triggered
    if (isExecuting && allSteps.length > 0) {
      const recentSteps = allSteps.filter(s => s.timestamp >= executionStartedAtRef.current);
      if (recentSteps.length > 0) {
        const lastStep = recentSteps[recentSteps.length - 1];
        if (lastStep.status === 'error') {
          setIsExecuting(false);
        } else if (lastStep.nodeType === 'end' && lastStep.status === 'completed') {
          // Don't auto-complete if this end node loops back to a paired start
          const endNode = nodesRef.current.find((n) => n.id === lastStep.nodeId);
          if (!endNode?.data?.config?.pairedStartId) {
            setIsExecuting(false);
          }
        }
      }
    }
  }, [serverDebugMode, workflowId, executionSteps, lastExecutionId, isExecuting, setNodes]);

  // Process device execution steps into debug state
  useEffect(() => {
    if (!debugModeEnabled || !selectedDeviceId) return;

    const device = devices.find(d => d.id === selectedDeviceId);
    if (!device) return;

    const steps = getExecutionSteps(device.deviceId);
    const debugState = new Map<string, { lastStatus: 'completed' | 'error' | 'started' | 'skipped' | null; executionCount: number }>();

    for (const step of steps) {
      const current = debugState.get(step.nodeId) || { lastStatus: null, executionCount: 0 };
      if (step.status === 'completed' || step.status === 'error') {
        current.executionCount++;
        current.lastStatus = step.status;
      }
      debugState.set(step.nodeId, current);
    }

    setNodeDebugState(debugState);
  }, [debugModeEnabled, selectedDeviceId, executionSteps, devices, getExecutionSteps, nodes]);

  // Apply debug state to nodes (device mode only)
  useEffect(() => {
    if (serverDebugMode) return;

    if (!debugModeEnabled) {
      setNodes(prev => prev.map(node => ({
        ...node,
        data: {
          ...node.data,
          debugStatus: undefined,
          executionCount: undefined,
        }
      })));
      return;
    }

    setNodes(prev => prev.map(node => {
      const debug = nodeDebugState.get(node.id);
      return {
        ...node,
        data: {
          ...node.data,
          debugStatus: debug?.lastStatus || null,
          executionCount: debug?.executionCount || 0,
        }
      };
    }));
  }, [debugModeEnabled, serverDebugMode, nodeDebugState, setNodes]);

  // ─── Core Handlers ──────────────────────────────────────────────────────

  const loadWorkflow = async (id: string) => {
    try {
      setIsLoading(true);
      setError(null);

      const workflowRoute = buildWorkflowApiRoute(currentContext, id);
      const result = await workflowApi.get<{ data: WorkflowDefinition }>(`/${workflowRoute}`);
      const workflowData = result.data;
      setWorkflow(workflowData);

      if (workflowData.definition?.nodes) {
        const rfNodes: Node<WorkflowNodeData>[] = workflowData.definition.nodes.map((node) => {
          let nodeConfig = node.config;
          if (node.type === 'condition' && node.config?.conditions && node.config.conditions.length > 0) {
            const firstCondition = node.config.conditions[0];
            nodeConfig = {
              ...node.config,
              field: firstCondition.field,
              operator: firstCondition.operator,
              value: firstCondition.value,
            };
          }
          return {
            id: node.id,
            type: 'custom',
            position: node.position,
            data: {
              label: node.data?.label || node.id,
              type: node.type,
              description: node.data?.description,
              config: nodeConfig,
              parameters: node.config?.parameters || {},
            },
          };
        });
        setNodes(rfNodes);
      }

      if (workflowData.definition?.edges) {
        const migrateSourceHandle = (handle: string | undefined): string => {
          if (!handle) return 'source-right';
          if (!handle.startsWith('source-')) return handle;
          return 'source-right';
        };
        const migrateTargetHandle = (handle: string | undefined): string => {
          if (!handle) return 'target-left';
          if (!handle.startsWith('target-')) return handle;
          return 'target-left';
        };

        const rfEdges = workflowData.definition.edges.map((edge) => ({
          id: edge.id,
          source: edge.source,
          target: edge.target,
          sourceHandle: migrateSourceHandle(edge.sourceHandle),
          targetHandle: migrateTargetHandle(edge.targetHandle),
          type: edge.type || 'smoothstep',
          animated: true,
          style: { stroke: '#3b82f6', strokeWidth: 2 },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: '#3b82f6',
            width: 20,
            height: 20,
          },
          data: edge.data,
        }));
        setEdges(rfEdges);
      }

      if (workflowData.definition?.variables) {
        setWorkflowVariables(workflowData.definition.variables);
      }

      if (workflowData.definition?.annotations?.length) {
        const annotationNodes: Node<WorkflowNodeData>[] = workflowData.definition.annotations.map((annotation) => ({
          id: annotation.id,
          type: `${annotation.type}_annotation`,
          position: annotation.position,
          data: { type: annotation.type, ...annotation.data },
          draggable: true,
          selectable: true,
          connectable: false,
          zIndex: annotation.type === 'rectangle' ? -1 : 0,
        }));
        setNodes(nds => [...nds, ...annotationNodes]);
      }

      if (workflowData.device_id) setSelectedDeviceId(workflowData.device_id);
      if (workflowData.targetVariant) {
        setSelectedVariantId(workflowData.targetVariant as DeviceVariantId);
      }

      if (workflowData.type === 'device') {
        setSelectedTarget(ExecutionTarget.ESP32);
      } else if (workflowData.type === 'data_source') {
        // Data Source workflows run on server but have a distinct type
        setSelectedTarget(ExecutionTarget.SERVER);
        setIsDataSource(true);
      } else {
        setSelectedTarget(ExecutionTarget.SERVER);
      }

      if (workflowData.debugMode !== undefined) {
        setServerDebugMode(workflowData.debugMode);
      }
    } catch (err) {
      console.error('Error loading workflow:', err);
      setError(err instanceof Error ? err.message : 'Failed to load workflow');
    } finally {
      setIsLoading(false);
    }
  };
  loadWorkflowRef.current = loadWorkflow;

  const onConnect = useCallback(
    (params: Connection) => {
      setEdges((eds) =>
        addEdge(
          {
            ...params,
            type: 'smoothstep',
            animated: true,
            style: { stroke: '#3b82f6', strokeWidth: 2 },
            markerEnd: {
              type: MarkerType.ArrowClosed,
              color: '#3b82f6',
              width: 20,
              height: 20,
            },
          },
          eds
        )
      );
    },
    [setEdges]
  );

  const onNodeClick = useCallback((_event: React.MouseEvent, _node: Node) => {
    setValidationResult(null);
  }, []);

  const onPaneClick = useCallback(() => {
    setValidationResult(null);
  }, []);

  const onSelectionChange = useCallback(({ nodes }: OnSelectionChangeParams) => {
    setSelectedNodes(nodes);
    setValidationResult(null);
  }, []);

  const onEdgeClick = useCallback((event: React.MouseEvent, _edge: Edge) => {
    event.stopPropagation();
  }, []);

  const onEdgesDelete = useCallback((edgesToDelete: Edge[]) => {
    setEdges((eds) => eds.filter((e) => !edgesToDelete.find((ed) => ed.id === e.id)));
  }, [setEdges]);

  const onReconnect = useCallback((oldEdge: Edge, newConnection: Connection) => {
    setEdges((eds) => {
      const filtered = eds.filter((e) => e.id !== oldEdge.id);
      return addEdge(newConnection, filtered);
    });
  }, [setEdges]);

  const onInit = useCallback((instance: ReactFlowInstance) => {
    reactFlowInstanceRef.current = instance;
  }, []);

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      let nodeType = event.dataTransfer.getData('application/reactflow');
      if (!nodeType) {
        const jsonData = event.dataTransfer.getData('application/json');
        if (jsonData) {
          try {
            const nodeData: unknown = JSON.parse(jsonData);
            const result = z.object({ type: z.string() }).passthrough().safeParse(nodeData);
            if (result.success) {
              nodeType = result.data.type;
            }
          } catch { /* ignore */ }
        }
      }
      if (!nodeType) return;

      const instance = reactFlowInstanceRef.current;
      if (!instance) return;

      const position = instance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      const registryNode = getNodeDefinition(nodeType);
      const localConfig = nodeTypeConfig[nodeType as keyof typeof nodeTypeConfig];
      const label = registryNode?.name || localConfig?.label || nodeType;

      const newNode: Node = {
        id: `${nodeType}_${Date.now()}`,
        type: 'custom',
        position,
        data: { label, type: nodeType, description: '', config: {}, parameters: {} },
      };
      setNodes((nds) => nds.concat(newNode));
    },
    [setNodes, getNodeDefinition]
  );

  const handleSave = async (): Promise<boolean> => {
    if (!workflowId) {
      setError('No workflow ID provided');
      return false;
    }
    try {
      setIsSaving(true);
      setError(null);

      const workflowNodes = nodes.filter(n => !n.type?.includes('annotation'));
      const annotationNodes = nodes.filter(n => n.type?.includes('annotation'));

      const backendNodes = workflowNodes.map((node) => {
        const baseNode = {
          id: node.id,
          type: node.data.type,
          position: node.position,
          data: { label: node.data.label, description: node.data.description },
          config: node.data.config || {},
        };
        if (node.data.type === 'condition' && node.data.config?.field && node.data.config?.operator) {
          const trueEdge = edges.find(e => e.source === node.id && e.sourceHandle === 'true');
          const falseEdge = edges.find(e => e.source === node.id && e.sourceHandle === 'false');
          baseNode.config = {
            ...node.data.config,
            conditions: [{ field: node.data.config.field, operator: node.data.config.operator, value: node.data.config.value }],
            operator: 'and',
            trueNode: trueEdge?.target,
            falseNode: falseEdge?.target,
          };
        }
        return baseNode;
      });

      const backendAnnotations = annotationNodes.map(a => ({
        id: a.id,
        type: a.type?.replace('_annotation', '') || 'rectangle',
        position: a.position,
        data: a.data,
      }));

      const backendEdges = edges.map((edge) => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        sourceHandle: edge.sourceHandle || null,
        targetHandle: edge.targetHandle || null,
        type: edge.type || 'default',
        data: edge.data || {},
      }));

      const definition = {
        nodes: backendNodes,
        edges: backendEdges,
        variables: workflowVariables,
        triggers: workflow?.definition?.triggers || [],
        annotations: backendAnnotations,
        version: workflow?.definition?.version || '1.0.0',
      };

      // Determine workflow type from isDataSource flag or execution target
      const workflowType = isDataSource
        ? 'data_source'
        : selectedTarget === ExecutionTarget.ESP32 ? 'device' : 'server';
      const workflowRoute = buildWorkflowApiRoute(currentContext, workflowId);
      await workflowApi.patch(`/${workflowRoute}`, {
        definition,
        device_id: selectedDeviceId || null,
        type: workflowType,
        targetVariant: selectedVariantId || null,
      });
      toast.success('Workflow saved successfully', { description: 'Your changes have been saved to the server' });
      return true;
    } catch (err) {
      console.error('Error saving workflow:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to save workflow';
      setError(errorMessage);
      toast.error('Failed to save workflow', { description: errorMessage });
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handlePublishToggle = async (shouldPublish: boolean) => {
    if (!workflowId) { setError('No workflow ID provided'); return; }

    // Validate Data Source workflows have a ui_data_output node before publishing
    if (shouldPublish && isDataSource) {
      const hasOutputNode = nodes.some((n) => n.data?.type === 'ui_data_output');
      if (!hasOutputNode) {
        toast.warning('Missing UI Data Output node', {
          description: 'Data Source workflows require a UI Data Output node to return data to components. Add one before publishing.',
        });
        return;
      }
    }

    try {
      setIsSaving(true);
      setError(null);
      const workflowRoute = buildWorkflowApiRoute(currentContext, workflowId);
      const result = await workflowApi.post<{ data: WorkflowDefinition }>(`/${workflowRoute}/publish`, { published: shouldPublish });
      if (result.data) setWorkflow(result.data);
      toast.success(shouldPublish ? 'Workflow published' : 'Workflow unpublished', {
        description: shouldPublish ? 'MQTT triggers and scheduled tasks are now active' : 'Workflow returned to draft status',
      });
    } catch (err) {
      console.error('Error updating workflow status:', err);
      toast.error('Failed to update workflow status', {
        description: err instanceof Error ? err.message : `Failed to ${shouldPublish ? 'publish' : 'unpublish'} workflow`,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleExecute = async () => {
    if (!workflowId) { toast.error('Cannot execute', { description: 'No workflow ID provided' }); return; }
    try {
      toast.info('Executing workflow...', { description: 'Starting workflow execution' });
      setIsExecuting(true);
      executionStartedAtRef.current = Date.now();
      const workflowRoute = buildWorkflowApiRoute(currentContext, workflowId);
      const result = await workflowApi.post<{ execution?: { id: string }; data?: { execution?: { id: string } } }>(`/${workflowRoute}/execute`, {
        triggerData: { executedFrom: 'editor', timestamp: new Date().toISOString() },
        priority: 'normal',
      });
      const executionId = result.execution?.id || result.data?.execution?.id;
      if (executionId) setLastExecutionId(executionId);
      toast.success('Workflow execution started', { description: `Execution ID: ${executionId || 'Unknown'}`, duration: 5000 });
    } catch (err) {
      console.error('Error executing workflow:', err);
      setIsExecuting(false);
      toast.error('Execution failed', { description: err instanceof Error ? err.message : 'Failed to execute workflow' });
    }
  };

  const handleCancelExecution = async () => {
    if (!lastExecutionId) { toast.error('No execution to cancel'); return; }
    try {
      await workflowApi.post(`/api/executions/${lastExecutionId}/cancel`);
      setIsExecuting(false);
      toast.success('Execution cancelled');
    } catch (err) {
      console.error('Error cancelling execution:', err);
      setIsExecuting(false);
      toast.error('Failed to cancel', { description: err instanceof Error ? err.message : 'Cancel failed' });
    }
  };

  const handleBack = () => {
    if (embedded && onClose) {
      onClose();
    } else {
      navigate('/workflow-configuration');
    }
  };

  const toggleServerDebugMode = async (enabled: boolean) => {
    if (!workflowId) { toast.error('Cannot toggle debug mode', { description: 'Please save the workflow first' }); return; }
    try {
      const route = buildWorkflowApiRoute(currentContext, workflowId);
      await workflowApi.patch(`/${route}/debug`, { debugMode: enabled });
      setServerDebugMode(enabled);
      toast.success(`Debug mode ${enabled ? 'enabled' : 'disabled'}`, {
        description: enabled ? 'You will see real-time step events when the workflow runs' : 'Step events will not be emitted during execution',
      });
    } catch (err) {
      console.error('Error toggling debug mode:', err);
      toast.error('Failed to update debug mode');
    }
  };

  const handleDeployToDevice = async () => {
    if (!workflowId) { toast.error('Cannot deploy', { description: 'Please save the workflow first' }); return; }
    if (!selectedDeviceId) { toast.error('Cannot deploy', { description: 'Please select a device first' }); return; }

    const device = devices.find(d => d.id === selectedDeviceId);
    if (!device) { toast.error('Cannot deploy', { description: 'Device not found' }); return; }

    if (device.status !== 'online' && device.status !== 'running') {
      toast.error('Cannot deploy', {
        description: device.status === 'deploying' ? 'A deployment is already in progress'
          : device.status === 'error' ? 'Device is in error state - please check device'
          : 'Device is not online'
      });
      return;
    }

    try {
      toast.info('Saving workflow...', { description: 'Saving before deployment' });
      const saveSuccess = await handleSave();
      if (!saveSuccess) { toast.error('Cannot deploy', { description: 'Failed to save workflow before deployment' }); return; }

      toast.info('Deploying workflow...', { description: `Deploying to ${device.name}` });
      const result = await deployWorkflow(selectedDeviceId, workflowId);

      if (result.success) {
        if (debugModeEnabled) {
          clearExecutionLogs(device.deviceId);
          setNodeDebugState(new Map());
        }
        if (result.queued) {
          toast.success('Deployment queued', { description: `Workflow will deploy when ${device.name} reconnects`, duration: 5000 });
        } else {
          toast.info('Deployment started', { description: `Waiting for ${device.name} to acknowledge...`, duration: 3000 });
        }
      } else {
        toast.error('Deployment failed', { description: 'Failed to deploy workflow to device' });
      }
    } catch (err) {
      console.error('Error deploying workflow:', err);
      toast.error('Deployment failed', { description: err instanceof Error ? err.message : 'Failed to deploy workflow' });
    }
  };

  // ─── Environments Dialog ────────────────────────────────────────────────

  const [environmentsDialogOpen, setEnvironmentsDialogOpen] = useState(false);
  const [availableEnvironments, setAvailableEnvironments] = useState<string[]>([]);

  const openEnvironmentsDialog = useCallback(() => setEnvironmentsDialogOpen(true), []);
  const closeEnvironmentsDialog = useCallback(() => setEnvironmentsDialogOpen(false), []);

  // Fetch available environments from all engine instances on mount
  useEffect(() => {
    engineApi.getInstances()
      .then((data) => {
        const envs = Array.from(new Set(
          (data?.instances || []).flatMap((instance) => instance.environments || [])
        )).sort();
        if (envs.length > 0) {
          setAvailableEnvironments(envs);
        }
      })
      .catch(() => { /* Engine may not be running */ });
  }, []);

  const handleSaveEnvironments = useCallback(async (environments: string[]) => {
    if (!workflowId) return;
    try {
      await engineApi.updateWorkflowEnvironments(workflowId, environments);
      setWorkflow((prev) => prev ? { ...prev, runEnvironments: environments } : prev);
      setEnvironmentsDialogOpen(false);
      toast.success('Run environments updated');
    } catch (err) {
      console.error('Failed to update environments:', err);
      toast.error('Failed to update environments');
    }
  }, [workflowId, setWorkflow]);

  // ─── Agent Conversion ───────────────────────────────────────────────────

  const convertToAgent = useCallback(async () => {
    if (!workflowId) return;
    try {
      const route = buildWorkflowApiRoute(currentContext, workflowId);
      await workflowApi.patch(`/${route}`, {
        type: 'agent',
        agentConfig: {
          autonomyLevel: 'guided',
          degradationPolicy: { fallbackDecision: 'fallback', queueExceptions: true },
          llmConfig: { routineModelTier: 3, escalationModelTier: 2, confidenceThreshold: 0.7, timeout: 30000, maxTokens: 2048 },
          capabilities: [],
          generation: 0,
        },
      });
      setWorkflow((prev) => prev ? { ...prev, type: 'agent', agentConfig: { autonomyLevel: 'guided', generation: 0 } } : prev);
      toast.success('Workflow converted to agent type');
    } catch (err) {
      console.error('Failed to convert to agent:', err);
      toast.error('Failed to convert to agent');
    }
  }, [currentContext, workflowId, setWorkflow]);

  // ─── Bridge to EditorLayout (embedded mode) ─────────────────────────────

  // Stabilise callback refs so the bridge effect doesn't fire every render
  const handleSaveRef = useRef(handleSave);
  const handleExecuteRef = useRef(handleExecute);
  const handlePublishToggleRef = useRef(handlePublishToggle);
  const openEnvironmentsDialogRef = useRef(openEnvironmentsDialog);
  handleSaveRef.current = handleSave;
  handleExecuteRef.current = handleExecute;
  handlePublishToggleRef.current = handlePublishToggle;
  openEnvironmentsDialogRef.current = openEnvironmentsDialog;

  // Bridge workflow actions to editor-store for EditorLayout header integration.
  // Only re-register when primitive values change, NOT on every render.
  useEffect(() => {
    if (!embedded) return;
    useEditorStore.getState().registerWorkflowActions({
      handleSave: () => handleSaveRef.current(),
      handleExecute: () => handleExecuteRef.current(),
      handlePublishToggle: (shouldPublish: boolean) => handlePublishToggleRef.current(shouldPublish),
      openEnvironmentsDialog: () => openEnvironmentsDialogRef.current(),
      workflowStatus: (workflow?.status as 'draft' | 'published') ?? null,
      selectedTarget,
      setSelectedTarget: setSelectedTarget as (t: string) => void,
      isSaving,
      error,
    });
    return () => useEditorStore.getState().clearWorkflowActions();
  }, [embedded, selectedTarget, setSelectedTarget, isSaving, error, workflow?.status]);

  // ─── Node Update Helpers ────────────────────────────────────────────────

  const updateNodeData = (field: string, value: JsonValue | undefined) => {
    if (!selectedNode) return;
    setNodes((nds) => nds.map((node) =>
      node.id === selectedNode.id ? { ...node, data: { ...node.data, [field]: value } } : node
    ));
  };

  const updateNodeParameter = (paramName: string, value: JsonValue | undefined) => {
    if (!selectedNode) return;
    setNodes((nds) => nds.map((node) =>
      node.id === selectedNode.id ? {
        ...node, data: { ...node.data, config: { ...node.data.config, parameters: { ...node.data.config?.parameters, [paramName]: value } } }
      } : node
    ));
  };

  const updateNodeConfig = <K extends keyof WorkflowNodeConfig,>(
    configName: K,
    value: WorkflowNodeConfig[K],
  ) => {
    if (!selectedNode) return;
    setNodes((nds) => nds.map((node) =>
      node.id === selectedNode.id ? {
        ...node, data: { ...node.data, config: { ...node.data.config, [configName]: value } }
      } : node
    ));
  };

  const deleteSelectedNodes = useCallback(() => {
    if (selectedNodes.length === 0) return;
    const nodeIdsToDelete = new Set(selectedNodes.map(n => n.id));

    selectedNodes.forEach(deletedNode => {
      const deletedNodeConfig = deletedNode.data.config;

      if (deletedNode.data.type === 'start' && deletedNodeConfig?.pairedEndId) {
        const pairedEndId = deletedNodeConfig.pairedEndId;
        if (!nodeIdsToDelete.has(pairedEndId)) {
          setNodes(nds => nds.map(n =>
            n.id === pairedEndId
              ? { ...n, data: { ...n.data, config: { ...n.data.config, pairedStartId: undefined, pairedStartName: undefined } } }
              : n
          ));
        }
      }
      if (deletedNode.data.type === 'end' && deletedNodeConfig?.pairedStartId) {
        const pairedStartId = deletedNodeConfig.pairedStartId;
        if (!nodeIdsToDelete.has(pairedStartId)) {
          setNodes(nds => nds.map(n =>
            n.id === pairedStartId
              ? { ...n, data: { ...n.data, config: { ...n.data.config, pairedEndId: undefined } } }
              : n
          ));
        }
      }
    });

    setNodes((nds) => nds.filter((n) => !nodeIdsToDelete.has(n.id)));
    setEdges((eds) => eds.filter((e) => !nodeIdsToDelete.has(e.source) && !nodeIdsToDelete.has(e.target)));
    setSelectedNodes([]);
  }, [selectedNodes, setNodes, setEdges]);

  // ─── Expression Helpers ─────────────────────────────────────────────────

  const getAvailableVariables = (): Array<{ name: string; type: string; source: string }> => {
    const availableVars: Array<{ name: string; type: string; source: string }> = [];
    workflowVariables.forEach(v => {
      availableVars.push({ name: v.name, type: v.type, source: 'Workflow Variable' });
    });
    let startNode = nodes.find(n => n.data?.type === 'start');
    if (selectedNode?.data?.type === 'start') startNode = selectedNode;
    if (startNode?.data?.config?.variableMappings) {
      startNode.data.config.variableMappings.forEach((mapping: WorkflowVariableMapping) => {
        if (mapping.targetVariable && mapping.targetVariable.trim() !== '') {
          availableVars.push({ name: mapping.targetVariable.trim(), type: 'string', source: 'MQTT Mapping' });
        }
      });
    }
    availableVars.push(
      { name: 'triggeredBy', type: 'string', source: 'Built-in' },
      { name: 'triggerTime', type: 'string', source: 'Built-in' },
      { name: 'triggerConfig', type: 'object', source: 'Built-in' },
      { name: 'triggerConfig.topic', type: 'string', source: 'Built-in' },
      { name: 'triggerConfig.message', type: 'object', source: 'Built-in' },
      { name: 'triggerConfig.type', type: 'string', source: 'Built-in' }
    );
    return availableVars;
  };

  const handleInsertVariable = (variableName: string) => {
    if (!expressionInputRef.current) return;
    const input = expressionInputRef.current;
    const start = input.selectionStart || 0;
    const end = input.selectionEnd || 0;
    const currentValue = selectedNode?.data.config?.expression || '';
    const newValue = currentValue.substring(0, start) + variableName + currentValue.substring(end);
    updateNodeConfig('expression', newValue);
    setShowVariablePicker(false);
    setTimeout(() => {
      if (expressionInputRef.current) {
        expressionInputRef.current.focus();
        const newCursorPos = start + variableName.length;
        expressionInputRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  const handleValidateExpression = async () => {
    if (!selectedNode?.data.config?.expression) { toast.error('No expression to validate'); return; }
    setIsValidating(true);
    setValidationResult(null);

    try {
      const contextVariables: Record<string, JsonValue | undefined> = {};
      workflowVariables.forEach(v => { contextVariables[v.name] = v.defaultValue; });
      let startNode = nodes.find(n => n.data?.type === 'start');
      if (selectedNode?.data?.type === 'start') startNode = selectedNode;
      if (startNode?.data?.config?.variableMappings) {
        startNode.data.config.variableMappings.forEach((mapping: WorkflowVariableMapping) => {
          if (mapping.targetVariable && mapping.targetVariable.trim() !== '') {
            contextVariables[mapping.targetVariable.trim()] = 'mqtt_placeholder_value';
          }
        });
      }
      contextVariables['triggeredBy'] = 'system';
      contextVariables['triggerTime'] = new Date().toISOString();
      contextVariables['triggerConfig'] = { topic: 'test/topic', message: {}, type: 'mqtt' };
      nodes.forEach(node => {
        if (node.data?.type === 'http_request') {
          contextVariables['httpResponse'] = {
            current: { temperature_2m: 20.5, humidity: 65, wind_speed: 10.2 },
            hourly: { temperature_2m: [18.5, 19.0, 20.5, 21.0], humidity: [70, 68, 65, 63] },
            data: {}, status: 200, headers: {}
          };
        }
      });

      let baseRoute: string;
      if (currentContext?.type === 'organization' && currentContext.orgSlug) {
        baseRoute = `api/orgs/${currentContext.orgSlug}/workflows/validate-expression`;
      } else {
        const userId = getCurrentWorkflowUserId();
        if (!userId) throw new Error('User not authenticated');
        baseRoute = `api/users/${userId}/workflows/validate-expression`;
      }

      const result = await workflowApi.post<{ data: WorkflowValidationResult }>(`/${baseRoute}`, {
        expression: selectedNode.data.config.expression,
        variables: contextVariables
      });

      if (result.data.valid) {
        setValidationResult({ valid: true, result: result.data.result });
        toast.success('Expression is valid!', { description: `Result: ${JSON.stringify(result.data.result)}`, duration: 5000 });
      } else {
        setValidationResult({ valid: false, error: result.data.error });
        toast.error('Expression validation failed', { description: result.data.error, duration: 5000 });
      }
    } catch (err) {
      console.error('Error validating expression:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to validate expression';
      setValidationResult({ valid: false, error: errorMessage });
      toast.error('Validation error', { description: errorMessage });
    } finally {
      setIsValidating(false);
    }
  };

  const extractJsonPaths = (obj: unknown, prefix = ''): string[] => {
    const paths: string[] = [];
    if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
      for (const [key, value] of Object.entries(obj)) {
        const path = prefix ? `${prefix}.${key}` : key;
        if (value === null || typeof value !== 'object') {
          paths.push(path);
        } else if (Array.isArray(value)) {
          paths.push(path);
          if (value.length > 0 && typeof value[0] === 'object') {
            paths.push(...extractJsonPaths(value[0], `${path}[0]`));
          }
        } else {
          paths.push(...extractJsonPaths(value, path));
        }
      }
    }
    return paths;
  };

  const pathToVariableName = (path: string): string => {
    return path
      .replace(/\[\d+\]/g, '')
      .split(/[._]/)
      .map((part, index) =>
        index === 0 ? part.toLowerCase() : part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
      )
      .join('');
  };

  const handleParseSampleMessage = () => {
    setSampleParseError(null);
    const sampleJson = selectedNode?.data?.config?.sampleMessage;
    if (!sampleJson?.trim()) { setSampleParseError('Please paste a sample MQTT message'); return; }
    try {
      const parsed: unknown = JSON.parse(sampleJson);
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        setSampleParseError('Sample must be a JSON object');
        return;
      }
      const paths = extractJsonPaths(parsed);
      if (paths.length === 0) { setSampleParseError('No fields found in sample message'); return; }
      const newMappings = paths.map(path => ({
        sourcePath: `triggerConfig.message.${path}`,
        targetVariable: pathToVariableName(path)
      }));
      updateNodeConfig('variableMappings', newMappings);
      toast.success(`Generated ${newMappings.length} variable mapping(s)`);
    } catch {
      setSampleParseError('Invalid JSON syntax');
    }
  };

  // ─── Context Value ──────────────────────────────────────────────────────

  const value: WorkflowEditorContextValue = {
    workflowId, workflow, setWorkflow, isLoading, isSaving, error, embedded, projectId,
    nodes, setNodes, onNodesChange, edges, setEdges, onEdgesChange,
    selectedNodes, setSelectedNodes, selectedNode,
    workflowVariables, setWorkflowVariables, allAvailableVariables,
    rightSidebarTab, setRightSidebarTab, leftSidebarTab, setLeftSidebarTab,
    validationResult, setValidationResult, isValidating,
    showVariablePicker, setShowVariablePicker, expressionInputRef,
    debugModeEnabled, setDebugModeEnabled, serverDebugMode,
    lastExecutionId, nodeDebugState, sampleParseError, setSampleParseError,
    effectiveExecutionKey, onDemandSchema,
    selectedDeviceId, setSelectedDeviceId, selectedVariantId, setSelectedVariantId,
    nodeRegistryLoading, selectedTarget, setSelectedTarget, isDataSource, setIsDataSource,
    getFilteredNodesByCategory, getNodeDefinition,
    devices, deployWorkflow, getExecutionSteps, clearExecutionLogs,
    setDeviceLogging, executionSteps, executionLogs,
    dataFilterOptions, fetchDataFilterOptions,
    screens, fetchScreens, openSchemaBuilder, schemas, fetchSchemas, getSchema,
    reactFlowWrapper,
    onConnect, onNodeClick, onPaneClick, onSelectionChange,
    onEdgeClick, onEdgesDelete, onReconnect, onInit, onDragOver, onDrop,
    handleSave, handleExecute, handleCancelExecution, isExecuting, handleBack, handleDeployToDevice, handlePublishToggle,
    handleParseSampleMessage, handleValidateExpression, handleInsertVariable,
    toggleServerDebugMode, deleteSelectedNodes,
    updateNodeData, updateNodeParameter, updateNodeConfig,
    getAvailableVariables, extractJsonPaths, pathToVariableName,
    environmentsDialogOpen, openEnvironmentsDialog, closeEnvironmentsDialog,
    availableEnvironments, handleSaveEnvironments,
    convertToAgent,
    onClose,
  };

  return (
    <WorkflowEditorContext.Provider value={value}>
      {children}
      <WorkflowEnvironmentsDialog
        open={environmentsDialogOpen}
        onOpenChange={(open) => open ? openEnvironmentsDialog() : closeEnvironmentsDialog()}
        workflowName={workflow?.name || 'Workflow'}
        currentEnvironments={workflow?.runEnvironments || []}
        availableEnvironments={availableEnvironments}
        isSaving={isSaving}
        onSave={handleSaveEnvironments}
      />
    </WorkflowEditorContext.Provider>
  );
};
